// Popover that shows the three attachment options (image / video / GIF).
// Each option is a label wrapping a hidden <input type="file"> so clicking
// a row opens the OS file picker filtered to the relevant accept list.

import { useId, useRef } from "react";
import { Image, Film, Sparkles, Paperclip } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export type AttachmentOption = {
  key:    "image" | "video" | "gif";
  label:  string;
  hint:   string;
  accept: string;
  Icon:   typeof Image;
};

const OPTIONS: ReadonlyArray<AttachmentOption> = [
  { key: "image", label: "相片", hint: "最多 2 MiB",      accept: "image/png,image/jpeg,image/webp,image/heic,image/heif", Icon: Image },
  { key: "video", label: "影片", hint: "最多 5 MiB",      accept: "video/mp4,video/quicktime,video/webm",               Icon: Film },
  { key: "gif",   label: "GIF",  hint: "最多 5 MiB",      accept: "image/gif",                                          Icon: Sparkles },
];

export function AttachmentTypePicker({
  disabled,
  onPick,
}: {
  disabled?: boolean;
  onPick: (file: File, option: AttachmentOption) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label="附加檔案"
        disabled={disabled}
        className={cn(
          "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foreground/70",
          "hover:bg-secondary disabled:pointer-events-none disabled:opacity-50",
        )}
      >
        <Paperclip className="h-5 w-5" aria-hidden />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-56 p-2"
      >
        <ul className="flex flex-col gap-0.5">
          {OPTIONS.map(opt => (
            <PickerRow key={opt.key} option={opt} onPick={onPick} />
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function PickerRow({
  option,
  onPick,
}: {
  option: AttachmentOption;
  onPick: (file: File, option: AttachmentOption) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const { Icon, label, hint, accept } = option;

  return (
    <li>
      <label
        htmlFor={inputId}
        className={cn(
          "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm",
          "hover:bg-secondary",
        )}
      >
        <span
          aria-hidden
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-secondary text-foreground/80"
        >
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex flex-1 flex-col">
          <span className="font-medium text-foreground">{label}</span>
          <span className="text-[11px] text-muted-foreground">{hint}</span>
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={e => {
            const f = e.currentTarget.files?.[0];
            if (f) onPick(f, option);
            e.currentTarget.value = ""; // reset so the same file can be re-picked
          }}
        />
      </label>
    </li>
  );
}
